import type { AssignmentScope } from '@Application/Ai/AiConfiguration/Assignments/AssignmentScope';
import ClearModelAssignment from '@Application/Ai/AiConfiguration/Assignments/Commands/ClearModelAssignment';
import SaveModelAssignment from '@Application/Ai/AiConfiguration/Assignments/Commands/SaveModelAssignment';
import clearModelAssignment from '@Application/Ai/AiConfiguration/Assignments/clearModelAssignment';
import saveModelAssignment from '@Application/Ai/AiConfiguration/Assignments/saveModelAssignment';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import writePermissionByScope from '@UI/Pages/AiAssignments/Shared/UsageAssignmentCard/UsageAssignmentCardActions/Helpers/writePermissionByScope';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import type ModelAssignmentView from '@UI/Store/AiConfiguration/Views/ModelAssignmentView';
import { css } from '@emotion/react';
import { useDispatch } from '@verclam/synapse';
import { ConfirmDialog, SelectButton } from '@verclam/ui-kit';
import * as React from 'react';

type LivePosition = 'inherit' | 'on' | 'off';
type LeavingPosition = Exclude<LivePosition, 'on'>;

const ACCOUNT_OPTIONS = Object.freeze([
	{ value: 'on', label: 'Oui' },
	{ value: 'off', label: 'Non' },
]);

const LEVEL_OPTIONS = Object.freeze([
	{ value: 'inherit', label: 'Comme le niveau au-dessus' },
	{ value: 'on', label: 'Oui' },
	{ value: 'off', label: 'Non' },
]);

const CONFIRM_HEADER_BY_POSITION: Readonly<Record<LeavingPosition, string>> = Object.freeze({
	off: 'Couper le direct',
	inherit: "Revenir à l'hérité",
});

const switchStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

const hintStyle = css`
	margin: 0;
`;

type Props = {
	readonly assignment: ModelAssignmentView;
	readonly scope: AssignmentScope;
	/** The model row, shown only while the live feature is on. */
	readonly children: React.ReactNode;
};

/**
 * Leaf action: dispatches itself, never a callback prop. Choosing "Oui" writes nothing — the live
 * feature only turns on once a model is saved, so the position is held locally until then. Leaving
 * "Oui" while a model is defined drops that model, hence the reversible confirmation.
 */
const LiveUsageSwitch: React.FC<Props> = ({ assignment, scope, children }) => {
	const dispatch = useDispatch();
	const canWrite = useHasPermission(writePermissionByScope[scope.type]);
	const [isEnabling, setEnabling] = React.useState<boolean>(false);
	const [pendingPosition, setPendingPosition] = React.useState<LeavingPosition | null>(null);

	const hasModel = assignment.ownModelId !== null;
	const position = React.useMemo<LivePosition>(() => {
		if (hasModel || isEnabling) {
			return 'on';
		}
		return assignment.hasOwnAssignment || scope.type === 'account' ? 'off' : 'inherit';
	}, [hasModel, isEnabling, assignment.hasOwnAssignment, scope.type]);

	const applyPosition = React.useCallback(
		(target: LeavingPosition) => {
			setEnabling(false);

			if (target === 'off') {
				if (assignment.hasOwnAssignment && assignment.ownModelId === null) {
					return;
				}
				dispatch(
					saveModelAssignment(new SaveModelAssignment(scope, assignment.usage.value, null)),
				).catch(ignoreHandledError);
				return;
			}

			if (assignment.hasOwnAssignment) {
				dispatch(clearModelAssignment(new ClearModelAssignment(scope, assignment.usage.value))).catch(
					ignoreHandledError,
				);
			}
		},
		[dispatch, scope, assignment.usage.value, assignment.hasOwnAssignment, assignment.ownModelId],
	);

	const handleChange = React.useCallback(
		(value: string | readonly string[] | null) => {
			if (value === 'on') {
				setEnabling(true);
			} else if (value === 'off' || value === 'inherit') {
				if (hasModel) {
					setPendingPosition(value);
				} else {
					applyPosition(value);
				}
			}
		},
		[hasModel, applyPosition],
	);

	const handleConfirmHide = React.useCallback(() => {
		setPendingPosition(null);
	}, []);

	const handleConfirmAccept = React.useCallback(() => {
		if (pendingPosition !== null) {
			applyPosition(pendingPosition);
		}
		setPendingPosition(null);
	}, [pendingPosition, applyPosition]);

	return (
		<div css={switchStyle}>
			<SelectButton
				name={`live-usage-${assignment.usage.value}`}
				label={`Direct pour ${assignment.usage.label}`}
				isLabelHidden
				allowEmpty={false}
				disabled={!canWrite}
				options={scope.type === 'account' ? ACCOUNT_OPTIONS : LEVEL_OPTIONS}
				optionLabel="label"
				optionValue="value"
				value={position}
				onChange={handleChange}
			/>
			<ConditionalLoader data={position === 'on'}>
				{children}
				<ConditionalLoader data={!hasModel}>
					<p css={hintStyle}>Choisissez un modèle : le direct s'active dès qu'il est choisi.</p>
				</ConditionalLoader>
			</ConditionalLoader>
			<ConfirmDialog
				blockScroll
				closable
				draggable={false}
				visible={pendingPosition !== null}
				onHide={handleConfirmHide}
				header={CONFIRM_HEADER_BY_POSITION[pendingPosition ?? 'off']}
				message={
					<div>
						<p>Le modèle défini à ce niveau sera retiré.</p>
						<p>Cette action est réversible : vous pourrez choisir un modèle à tout moment.</p>
					</div>
				}
				accept={handleConfirmAccept}
				reject={handleConfirmHide}
			/>
		</div>
	);
};

export default LiveUsageSwitch;
